import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getUserBets, getGame, claimDailyBonus } from '../lib/bettingApi';
import { calculateWinLoss } from '../utils/betting';
import './Notifications.css';

function Notifications({ user, onGameClick, isOpen, onClose, onUnreadCountChange }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);
  
  // Daily bonus state
  const [canClaimBonus, setCanClaimBonus] = useState(false);
  const [nextClaimTime, setNextClaimTime] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimSuccess, setClaimSuccess] = useState(false);

  // Close dropdown when clicking outside or tapping outside (mobile)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      // Support both mouse and touch events for mobile
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Fetch notifications and check bonus eligibility
  useEffect(() => {
    if (!user || !isOpen) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    fetchNotifications();
    checkBonusEligibility();
  }, [user, isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all ended games where user has bets
      // First, get all user's bets
      const { data: allBets, error: betsError } = await supabase
        .from('bets')
        .select('game_id')
        .eq('user_id', user.id);

      if (betsError) throw betsError;

      if (!allBets || allBets.length === 0) {
        setNotifications([]);
        setLoading(false);
        return;
      }

      // Get unique game IDs
      const gameIds = [...new Set(allBets.map(bet => bet.game_id))];

      // Get all ended games from those IDs
      const { data: endedGames, error: gamesError } = await supabase
        .from('games')
        .select('*')
        .in('id', gameIds)
        .eq('status', 'ended')
        .not('winner_side', 'is', null)
        .order('updated_at', { ascending: false })
        .limit(50); // Limit to 50 most recent

      if (gamesError) throw gamesError;

      if (!endedGames || endedGames.length === 0) {
        setNotifications([]);
        setLoading(false);
        return;
      }

      // For each game, get user's bets and calculate win/loss
      const notificationsData = await Promise.all(
        endedGames.map(async (game) => {
          try {
            const userBets = await getUserBets(game.id);
            if (!userBets || userBets.length === 0) {
              return null;
            }

            const winLoss = calculateWinLoss(userBets, game.winner_side);
            const winningTeamName = game.winner_side === 'yes'
              ? (game.team_yes_name || 'Yes')
              : (game.team_no_name || 'No');

            // Check if notification was viewed
            const viewed = localStorage.getItem(`notification_viewed_${game.id}`) === 'true';

            return {
              game,
              userBets,
              winLoss,
              winningTeamName,
              viewed,
              endedAt: game.updated_at || game.ends_at
            };
          } catch (err) {
            console.error(`Error processing game ${game.id}:`, err);
            return null;
          }
        })
      );

      // Filter out nulls, filter out viewed notifications, and sort by ended date (newest first)
      const validNotifications = notificationsData
        .filter(n => n !== null && !n.viewed) // Only show unviewed notifications
        .sort((a, b) => {
          const dateA = new Date(a.endedAt);
          const dateB = new Date(b.endedAt);
          return dateB - dateA;
        });

      setNotifications(validNotifications);
      
      // Notify parent of unread count
      const unreadCount = validNotifications.filter(n => !n.viewed).length;
      if (onUnreadCountChange) {
        onUnreadCountChange(unreadCount);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as viewed (for unread count tracking)
    localStorage.setItem(`notification_viewed_${notification.game.id}`, 'true');
    
    // Remove notification from list
    setNotifications(prev => {
      const updated = prev.filter(n => n.game.id !== notification.game.id);
      
      // Notify parent of new unread count
      const unreadCount = updated.filter(n => !n.viewed).length;
      if (onUnreadCountChange) {
        onUnreadCountChange(unreadCount);
      }
      
      return updated;
    });

    // Navigate to game
    if (onGameClick) {
      onGameClick(notification.game);
    }
    onClose();
  };

  // Check if user is eligible for daily bonus
  const checkBonusEligibility = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_wallet')
        .select('last_daily_bonus_claimed')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking bonus eligibility:', error);
        return;
      }

      if (!data || !data.last_daily_bonus_claimed) {
        // Never claimed before - eligible
        setCanClaimBonus(true);
        setNextClaimTime(null);
        return;
      }

      const lastClaim = new Date(data.last_daily_bonus_claimed);
      const now = new Date();
      const hoursSinceLastClaim = (now.getTime() - lastClaim.getTime()) / (1000 * 60 * 60);

      if (hoursSinceLastClaim >= 24) {
        // 24 hours have passed - eligible
        setCanClaimBonus(true);
        setNextClaimTime(null);
      } else {
        // Not eligible yet - calculate next claim time
        setCanClaimBonus(false);
        const nextClaim = new Date(lastClaim.getTime() + 24 * 60 * 60 * 1000);
        setNextClaimTime(nextClaim);
      }
    } catch (err) {
      console.error('Error checking bonus eligibility:', err);
    }
  };

  // Format time until next claim
  const formatTimeUntil = (targetDate) => {
    const now = new Date();
    const diff = targetDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Now';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Handle claiming daily bonus
  const handleClaimBonus = async () => {
    setIsClaiming(true);
    setClaimError('');
    setClaimSuccess(false);

    try {
      const result = await claimDailyBonus();
      
      if (result.success) {
        setClaimSuccess(true);
        setCanClaimBonus(false);
        if (result.nextClaimTime) {
          setNextClaimTime(result.nextClaimTime);
        }
        
        // Refresh eligibility after a short delay
        setTimeout(() => {
          checkBonusEligibility();
        }, 1000);
        
        // Hide success message after 3 seconds
        setTimeout(() => setClaimSuccess(false), 3000);
      } else {
        setClaimError(result.error || 'Failed to claim daily bonus');
        if (result.nextClaimTime) {
          setNextClaimTime(result.nextClaimTime);
          setCanClaimBonus(false);
        }
      }
    } catch (error) {
      setClaimError(error.message || 'Failed to claim daily bonus');
    } finally {
      setIsClaiming(false);
    }
  };

  // Update countdown timer every second
  useEffect(() => {
    if (!nextClaimTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = nextClaimTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        // Time has passed - user can claim now
        setCanClaimBonus(true);
        setNextClaimTime(null);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [nextClaimTime]);

  const unreadCount = notifications.filter(n => !n.viewed).length;

  if (!isOpen) return null;

  return (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <div className="notifications-header">
        <h3>Game Results</h3>
        {unreadCount > 0 && (
          <span className="notifications-unread-badge">{unreadCount}</span>
        )}
      </div>

      <div className="notifications-content">
        {/* Daily Bonus Section */}
        <div className="daily-bonus-section-notifications">
          <h4 className="daily-bonus-title-notifications">Daily Bonus</h4>
          {claimSuccess && (
            <div className="daily-bonus-success-notifications">
              🎉 Successfully claimed $10 daily bonus!
            </div>
          )}
          {claimError && (
            <div className="daily-bonus-error-notifications">{claimError}</div>
          )}
          <button
            className={`daily-bonus-btn-notifications ${canClaimBonus ? 'enabled' : 'disabled'}`}
            onClick={handleClaimBonus}
            disabled={!canClaimBonus || isClaiming}
          >
            {isClaiming ? 'Claiming...' : canClaimBonus ? 'Claim $10 Daily Bonus' : 'Bonus Not Available'}
          </button>
          {nextClaimTime && (
            <div className="daily-bonus-countdown-notifications">
              Next claim available in: {formatTimeUntil(nextClaimTime)}
            </div>
          )}
        </div>

        {/* Divider */}
        {notifications.length > 0 && (
          <div className="notifications-divider"></div>
        )}

        {/* Game Results */}
        {loading ? (
          <div className="notifications-loading">Loading...</div>
        ) : error ? (
          <div className="notifications-error">{error}</div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <p>No ended games with bets yet.</p>
            <p>Your results will appear here when games end.</p>
          </div>
        ) : (
          <div className="notifications-list">
            {notifications.map((notification) => {
              const { game, winLoss, winningTeamName, viewed } = notification;
              const isWin = winLoss.won;
              const isLoss = !isWin && winLoss.payoutDifference < 0;
              const isBreakEven = winLoss.payoutDifference === 0;

              return (
                <div
                  key={game.id}
                  className={`notification-item ${!viewed ? 'unread' : ''} ${isWin ? 'win' : isLoss ? 'loss' : 'break-even'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {isWin && '🎉'}
                    {isLoss && '😔'}
                    {isBreakEven && '🤝'}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title-wrapper">
                      {game.title_image && (
                        <img
                          src={game.title_image}
                          alt={game.title}
                          className="notification-title-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="notification-title">{game.title}</div>
                    </div>
                    <div className="notification-details">
                      <span className="notification-team">Winner: {winningTeamName}</span>
                      <span className={`notification-amount ${isWin ? 'positive' : isLoss ? 'negative' : 'neutral'}`}>
                        {isWin && '+'}
                        {isLoss && '-'}
                        ${Math.abs(winLoss.payoutDifference).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  {!viewed && <div className="notification-dot"></div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Notifications;

